// src/hooks/useTableCellEditing.js
import { useState, useEffect, useRef, useCallback } from "react";

export const useTableCellEditing = ({
  onUpdateCell, // Callback from BaseTable to actually save the data
  isAnalyticsTable,
  formattedData, // For analytics table raw value logic
}) => {
  const [editingCell, setEditingCell] = useState({
    rowId: null,
    columnId: null,
  });
  const [tempValue, setTempValue] = useState("");
  const [originalValue, setOriginalValue] = useState("");
  const editRef = useRef(null);

  const startEditingCell = useCallback(
    (rowId, columnId, currentValue) => {
      // Logic for starting an edit (copied from BaseTable's current startEditingCell)
      // This includes handling specific logic for isAnalyticsTable
      if (
        isAnalyticsTable &&
        columnId.startsWith("P") &&
        columnId.includes("_")
      ) {
        setEditingCell({ rowId, columnId });
        const rawValueKey = `raw_${columnId}`;
        // Ensure formattedData is available and is an array
        const currentFormattedData = Array.isArray(formattedData)
          ? formattedData
          : [];
        const rawValue = currentFormattedData.find((row) => row.id === rowId)?.[
          rawValueKey
        ];
        setOriginalValue(rawValue ?? "");
        setTempValue(rawValue ?? "");
        return;
      }
      if (
        !isAnalyticsTable &&
        (columnId.startsWith("P") ||
          columnId.startsWith("F") ||
          columnId === "output")
      ) {
        return; // Don't edit these
      }
      setEditingCell({ rowId, columnId });
      setOriginalValue(currentValue ?? "");
      setTempValue(currentValue ?? "");
    },
    [isAnalyticsTable, formattedData]
  );

  const commitCellUpdate = useCallback(
    async (directNewValue) => {
      if (!editingCell.rowId || !editingCell.columnId) return;

      const { rowId, columnId } = editingCell;

      // Determine the value to save:
      const valueToSave =
        directNewValue !== undefined ? directNewValue : tempValue;

      // This condition might prevent the update if valueToSave ends up being the same as originalValue
      // AND directNewValue was undefined.
      if (valueToSave === originalValue && directNewValue === undefined) {
        setEditingCell({ rowId: null, columnId: null });
        return;
      }

      if (typeof onUpdateCell === "function") {
        try {
          await onUpdateCell(rowId, columnId, valueToSave, originalValue);
          setEditingCell({ rowId: null, columnId: null });
        } catch (error) {
          console.error("[Hook] Error during onUpdateCell:", error);
          setEditingCell({ rowId: null, columnId: null });
        }
      } else {
        setEditingCell({ rowId: null, columnId: null });
      }
    },
    [editingCell, tempValue, originalValue, onUpdateCell, setEditingCell]
  );

  useEffect(() => {
    if (!editingCell.rowId) return;

    const handleGlobalMouseDown = (event) => {
      if (event.button !== 0) return;
      if (editRef.current && !editRef.current.contains(event.target)) {
        commitCellUpdate();
      }
    };
    document.addEventListener("mousedown", handleGlobalMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleGlobalMouseDown);
    };
  }, [editingCell, commitCellUpdate]);

  const cancelCellEdit = useCallback(() => {
    setEditingCell({ rowId: null, columnId: null });
  }, []);

  return {
    editingCell,
    tempValue,
    setTempValue,
    // originalValue, // Not typically needed by consuming components
    editRef,
    startEditingCell,
    commitCellUpdate,
    cancelCellEdit,
    setEditingCell, // Exposing this for direct control if needed (e.g. from TableCell on Esc)
  };
};
